import { useAuth } from "@clerk/react";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";

import { useUser } from "@/web/auth/useUser";
import { toaster } from "@/web/components/primitives/toast";
import {
  canSubmitPledge,
  PledgeDialog,
  type PledgeFormState,
} from "@/web/components/styled/PledgeForm";
import {
  buildPledgeFormState,
  hadExistingPledge,
  pledgeFormHasChanges,
  pledgeFormSubmitFields,
} from "@/web/components/styled/pledgeFormState";
import { ErrorHandler } from "@/web/lib/errors";
import { useReducerMutation } from "@/web/lib/tanstack";
import { SharableBusinessCardPledgeDialog } from "@/web/routes/browse/-components/cards/SharableBusinessCardCard";
import { UnifiedBrowseCard } from "@/web/routes/browse/-components/cards/UnifiedBrowseCard";
import type { NominatedBrowseResult } from "@/web/routes/browse/-components/types";
import { reducers } from "@tycho/web-bindings";

export function NominatedCard({ browseResult }: { browseResult: NominatedBrowseResult }) {
  const { nominatedBusiness } = browseResult;
  const isClaimed = browseResult.type === "claimedNominatedBusiness";

  // A claimed nomination whose brand has a live sharable card is folded into
  // THIS row — the standalone card result (and its `/ttw/<cardId>` link) is
  // dropped by `mapCampaignBrowseRows`. When that linked card has a real TTW
  // page, this row owns the only path to it and must navigate there.
  //
  // Password-protected cards still link, matching `SharableBusinessCardCard`:
  // the access-code gate lives on `/ttw/<cardId>`, not here — we don't rebuild
  // the password flow onto `/browse`.
  const linkedCardId = browseResult.linkedCardId;
  const isLocked = browseResult.linkedCardRequiresAccessCode ?? false;
  const linkedCardHasTtwPage = browseResult.linkedCardHasTtwPage ?? false;
  const hasTtwPageTargetProperty = browseResult.linkedCardHasTtwPageTargetProperty ?? false;

  // A claimed business whose folded PUBLIC card has no TTW page has nothing to
  // link out to, but its standalone card row was swallowed by the fold — so this
  // row is the only card entry point from `/browse`. Its pledge must target the
  // CARD, not the nomination: a nomination EOI would be stored against the wrong
  // record and diverge from the card's own totals. Rows without a linked card
  // keep the nomination pledge flow (a nomination EOI is distinct from a card
  // pledge).
  const useCardPledge = linkedCardId !== undefined && !(linkedCardHasTtwPage || isLocked);

  const [pledgeDialogOpen, setPledgeDialogOpen] = useState(false);
  const openPledgeDialog = useCallback(() => {
    setPledgeDialogOpen(true);
  }, []);

  const planningCardBody = hasTtwPageTargetProperty ? (
    <UnifiedBrowseCard
      entity="deal"
      stage="planning"
      name={nominatedBusiness.name}
      address={browseResult.linkedCardTargetLocation}
      category={browseResult.businessTypeText || undefined}
      imageCdnPath={nominatedBusiness.heroCdnPath}
      tint={nominatedBusiness.colors?.primary}
      nominatedByName={browseResult.nominatedByName}
      backerCount={browseResult.totalInterested}
      maskBackers={isLocked}
      locked={isLocked}
      data-testid={`nominated-card-${nominatedBusiness.id}`}
    />
  ) : (
    <UnifiedBrowseCard
      entity="business"
      stage="planning"
      name={nominatedBusiness.name}
      category={browseResult.businessTypeText || undefined}
      imageCdnPath={nominatedBusiness.heroCdnPath}
      tint={nominatedBusiness.colors?.primary}
      backerCount={browseResult.totalInterested}
      maskBackers={isLocked}
      locked={isLocked}
      data-testid={`nominated-card-${nominatedBusiness.id}`}
    />
  );
  const cardBody = isClaimed ? (
    planningCardBody
  ) : (
    <UnifiedBrowseCard
      entity="deal"
      stage="nominated"
      name={nominatedBusiness.name}
      address={browseResult.location}
      category={browseResult.businessTypeText || undefined}
      imageCdnPath={nominatedBusiness.heroCdnPath}
      tint={nominatedBusiness.colors?.primary}
      nominatedByName={browseResult.nominatedByName}
      data-testid={`nominated-card-${nominatedBusiness.id}`}
    />
  );

  return (
    <>
      {/* `data-browse-card-action` lets the browse map forward a pin click to
          this card's primary action — link navigation when the row owns a card
          with a real TTW page (or a password-protected card), otherwise opening
          the pledge dialog. */}
      {linkedCardId !== undefined && (linkedCardHasTtwPage || isLocked) ? (
        <Link
          to="/ttw/$slug"
          params={{ slug: linkedCardId }}
          target="_blank"
          rel="noopener noreferrer"
          className="block transition-opacity hover:opacity-95 focus-visible:outline-2 focus-visible:outline-offset-2"
          aria-label={`View ${nominatedBusiness.name} page${isLocked ? " (password required)" : ""}`}
          data-browse-card-action
        >
          {cardBody}
        </Link>
      ) : (
        <div
          role="button"
          tabIndex={0}
          onClick={openPledgeDialog}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") openPledgeDialog();
          }}
          className="cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2"
          data-browse-card-action
        >
          {cardBody}
        </div>
      )}

      {useCardPledge && linkedCardId !== undefined ? (
        <SharableBusinessCardPledgeDialog
          open={pledgeDialogOpen}
          onOpenChange={setPledgeDialogOpen}
          cardId={linkedCardId}
          businessName={nominatedBusiness.name ?? "this business"}
          initialInterest={browseResult.linkedCardUserInterest}
        />
      ) : (
        <NominatedPledgeDialog
          open={pledgeDialogOpen}
          onOpenChange={setPledgeDialogOpen}
          nominatedBusinessId={browseResult.nominatedBusiness.id}
          businessName={nominatedBusiness.name ?? "this business"}
          initialAmount={browseResult.userInterestDollars}
          initialReason={browseResult.userReason}
          initialFirstName={browseResult.userFirstName}
          initialLastName={browseResult.userLastName}
          initialConsentToDisplay={browseResult.userConsentToDisplay}
        />
      )}
    </>
  );
}

/**
 * Modal that records / updates the current user's nomination EOI via the shared
 * <PledgeDialog>. The `expressInterestInNomination` reducer is an upsert, so the
 * same dialog handles "first pledge" and "edit existing pledge" — we pre-fill
 * from `initialAmount` / `initialReason`.
 *
 * Field parity note: the shared dialog's free-text `supportiveMessage` maps to
 * this flow's `reason` column. Anonymous visitors additionally supply name +
 * email (the email keys the EOI and is back-associated on later sign-in).
 */
function NominatedPledgeDialog({
  open,
  onOpenChange,
  nominatedBusinessId,
  businessName,
  initialAmount,
  initialReason,
  initialFirstName,
  initialLastName,
  initialConsentToDisplay,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nominatedBusinessId: string;
  businessName: string;
  initialAmount: number | undefined;
  initialReason: string | undefined;
  initialFirstName: string | undefined;
  initialLastName: string | undefined;
  initialConsentToDisplay: boolean | undefined;
}) {
  const { isSignedIn } = useAuth();
  const user = useUser();
  const [formState, setFormState] = useState<PledgeFormState>(() => buildPledgeFormState({}, user));
  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  const initialFormStateRef = useRef<PledgeFormState | null>(null);

  useEffect(() => {
    if (open) {
      const initial = buildPledgeFormState(
        {
          amount: initialAmount,
          firstName: initialFirstName,
          lastName: initialLastName,
          supportiveMessage: initialReason,
          consentToDisplay: initialConsentToDisplay,
        },
        user,
      );
      setFormState(initial);
      initialFormStateRef.current = initial;
    } else {
      initialFormStateRef.current = null;
    }
  }, [
    open,
    initialAmount,
    initialReason,
    initialFirstName,
    initialLastName,
    initialConsentToDisplay,
    user,
  ]);

  const { mutateAsync: expressInterest, isPending } = useReducerMutation(
    reducers.expressInterestInNomination,
  );

  const hasChanges = pledgeFormHasChanges(formState, initialFormStateRef.current);
  const canSubmit = !isPending && hasChanges && canSubmitPledge(formState, isSignedIn ?? false);

  const handleSubmit = useCallback(async () => {
    if (formState.amount === undefined) return;
    if (isPending || !canSubmitPledge(formState, isSignedIn ?? false)) return;
    const fields = pledgeFormSubmitFields(formState, isSignedIn ?? false);
    try {
      await expressInterest({
        nominatedBusinessId,
        intendedInvestmentDollars: formState.amount,
        reason: fields.supportiveMessage,
        email: fields.email,
        firstName: fields.firstName,
        lastName: fields.lastName,
        consentToDisplay: fields.consentToDisplay,
      });
      toaster({
        message: hadExistingPledge(initialFormStateRef.current)
          ? "Your pledge has been updated."
          : "Thanks — we've recorded your pledge.",
        variant: "success",
      });
      onOpenChange(false);
      if (!isSignedIn) setAuthPromptOpen(true);
    } catch (error) {
      ErrorHandler.default(error);
    }
  }, [expressInterest, formState, isPending, isSignedIn, nominatedBusinessId, onOpenChange]);

  return (
    <PledgeDialog
      open={open}
      onOpenChange={onOpenChange}
      formState={formState}
      onFormStateChange={setFormState}
      onSubmit={() => void handleSubmit()}
      isPending={isPending}
      isSignedIn={isSignedIn ?? false}
      currentPledgeAmount={initialAmount}
      businessName={businessName}
      canSubmit={canSubmit}
      authPromptOpen={authPromptOpen}
      onAuthPromptOpenChange={setAuthPromptOpen}
    />
  );
}
